// The alignments in force, by id. Each alignment's definition lives in its
// own file under alignments/<planet>/ and is registered here.
import type { AlignmentId } from './catalogue';
import { planetOrder } from './catalogue';
import { abyss } from './neptune/abyss';
import { balance } from './saturn/balance';
import { beginningsAndEnds } from './pluto/beginnings-and-ends';
import { breadth } from './jupiter/breadth';
import { cacophony } from './uranus/cacophony';
import { capability } from './saturn/capability';
import { climax } from './venus/climax';
import { collateralDamage } from './mars/collateral-damage';
import { crossroads } from './mercury/crossroads';
import { depth } from './neptune/depth';
import { discipline } from './saturn/discipline';
import { diversity } from './uranus/diversity';
import { elitism } from './jupiter/elitism';
import { embrace } from './venus/embrace';
import { enormity } from './jupiter/enormity';
import { explosiveness } from './uranus/explosiveness';
import { farReaches } from './pluto/far-reaches';
import { firstImpression } from './venus/first-impression';
import { foundations } from './saturn/foundations';
import { greatRedSpot } from './jupiter/great-red-spot';
import { javelin } from './mars/javelin';
import { majesty } from './jupiter/majesty';
import { mellifluence } from './venus/mellifluence';
import { orderliness } from './saturn/orderliness';
import { perception } from './mercury/perception';
import { rarities } from './uranus/rarities';
import { retrograde } from './mercury/retrograde';
import { riches } from './pluto/riches';
import { sacrifice } from './neptune/sacrifice';
import { seeingDouble } from './mercury/seeing-double';
import { solitude } from './neptune/solitude';
import { surety } from './mercury/surety';
import { symmetry } from './venus/symmetry';
import { transformation } from './pluto/transformation';
import { trident } from './neptune/trident';
import { ultimatum } from './mars/ultimatum';
import { underdog } from './pluto/underdog';
import { uniqueness } from './uranus/uniqueness';
import { volatility } from './mars/volatility';
import { wrath } from './mars/wrath';
import type { AlignmentContext, AlignmentDef } from './types';

const DEFS: readonly AlignmentDef[] = [
  retrograde, crossroads, surety, seeingDouble, perception,
  mellifluence, firstImpression, symmetry, embrace, climax,
  collateralDamage, volatility, wrath, javelin, ultimatum,
  enormity, majesty, elitism, breadth, greatRedSpot,
  orderliness, capability, foundations, discipline, balance,
  diversity, rarities, cacophony, explosiveness, uniqueness,
  sacrifice, depth, solitude, trident, abyss,
  transformation, beginningsAndEnds, underdog, farReaches, riches,
];

export const ALIGNMENTS: Readonly<Record<AlignmentId, AlignmentDef>> = Object.fromEntries(DEFS.map((d) => [d.id, d])) as Record<AlignmentId, AlignmentDef>;

export function alignmentDef(id: AlignmentId): AlignmentDef {
  return ALIGNMENTS[id];
}

/** The definitions in force, planet by planet from the Sun (the order the base-value sets apply in). */
export function defsInForce(ctx: AlignmentContext): AlignmentDef[] {
  const defs: AlignmentDef[] = [];
  for (const id of ctx.inForce) defs.push(ALIGNMENTS[id]);
  return defs.sort((a, b) => planetOrder(a.planet) - planetOrder(b.planet));
}
